/**
 * Centralized navigation data for the Partner Portal platform.
 *
 * This is the single source of truth for all sidebar navigation items AND
 * route-prefix ownership across every SPA in the platform.
 *
 * To add a new nav item:
 *  1. Add it to sidebar.json.
 *  2. Add its route prefix to the owning SPA's key in APP_ROUTE_PREFIXES below.
 *  3. No changes needed in any other repo — all SPAs import from @lucid/lego.
 */
import type { NavigationItem } from './Sidebar';
import sidebarJson from './sidebar.json';

export const navigationItems: NavigationItem[] = sidebarJson.data as NavigationItem[];

/**
 * Centralized route-prefix registry for cross-app navigation.
 *
 * The Sidebar uses the relevant key to decide whether a nav click should be
 * handled by React Router (client-side) or trigger a full page load (cross-app).
 *
 * Rules:
 *  - Root path `/` is always client-side (handled as a special case in Sidebar).
 *  - Any path whose prefix matches an entry in the app's own key → client-side navigate.
 *  - Everything else → `location.href` full page load.
 */
export const APP_ROUTE_PREFIXES = {
  /**
   * Routes owned by lucid-partner-portal.
   * '/' represents the root/dashboard route — it is matched as an exact path in Sidebar,
   * not as a startsWith prefix, so it does not accidentally match all routes.
   */
  partnerPortal: ['/', '/admin', '/prospect-desk', '/order-hub', '/login', '/accept-invitation'],

  /**
   * Routes owned by lucid-partner-portal-services-frontend.
   */
  services: [
    '/parts-center',
    '/parts-store',
    '/service-center',
    '/claims-center',
    '/vehicle-hub',
    '/customer-care',
    '/inbox',
    '/notifications',
    '/track-orders',
  ],

  /**
   * Routes owned by lucid-partner-portal-importer-frontend.
   */
  importer: ['/registration-desk', '/accounts-desk'],
};

export type AppKey = keyof typeof APP_ROUTE_PREFIXES;
